import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/authService';
import {
  APP_PRODUCT_NAME,
  COMPANY_LOGO,
  POWERED_BY_LOGO,
  POWERED_BY_NAME,
} from '../config/brandingConfig';
import { getAuthErrorMessage, PASSWORD_HINT } from '../utils/authApiErrors';
import './Login.css';

const parseStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

const ChangePassword = ({ forced = false }) => {
  const navigate = useNavigate();
  const storedUser = parseStoredUser();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCompanyLogo, setShowCompanyLogo] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    setLoading(true);
    try {
      if (forced) {
        const response = await authAPI.changeRequiredPassword(newPassword);
        const token = response?.data?.token;
        const user = response?.data?.user;
        if (token && user) {
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(user));
        } else {
          const updatedUser = { ...storedUser, must_change_password: false };
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      } else {
        await authAPI.changePassword({ currentPassword, newPassword });
        const updatedUser = { ...storedUser, must_change_password: false };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(getAuthErrorMessage(err, 'Unable to update password. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="logo">
            <div className="logo-icon">
              {showCompanyLogo && (
                <img src={COMPANY_LOGO} alt="Company Logo" onError={() => setShowCompanyLogo(false)} />
              )}
            </div>
            <h2 className="logo-title">{APP_PRODUCT_NAME}</h2>
          </div>
          <p className="subtitle">{forced ? 'Set your new password to continue' : 'Change password'}</p>
        </div>

        {forced && (
          <div className="alert alert-info" style={{ background: '#F5F3FF', border: '1px solid #bfdbfe', color: '#6D28D9', padding: '12px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
            Welcome{storedUser?.name ? `, ${storedUser.name}` : ''}. You must change your temporary password before using the portal.
          </div>
        )}

        {error && (
          <div className="alert alert-error">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        <form className="login-form" onSubmit={handleSubmit}>
          {!forced && (
            <div className="form-group">
              <label htmlFor="currentPassword">Current password <span className="req">*</span></label>
              <input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="newPassword">New password <span className="req">*</span></label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={PASSWORD_HINT}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm new password <span className="req">*</span></label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="btn-login" disabled={loading}>
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>

        <div className="login-footer-note" style={{ marginTop: 16 }}>
          <button type="button" className="login-link-btn" onClick={() => authAPI.logout()}>Sign out</button>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
